import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import {
  getUpsellEligibility,
  nabestelOndertapijtVerzenden,
  type UpsellEligibility,
  type NabestelOndertapijtResult,
} from '../../lib/upsell';
import { formatCurrency } from '../../lib/format';

interface Props {
  orderId: string;
  onClose: () => void;
  /** Aangeroepen na een geslaagde nabestelling (ververst de order). */
  onDone: () => void;
}

// Verzendkosten (PostNL) — altijd doorberekenen bij nabestellen na aflevering
// (de 30%-actie gaat normaal gratis met het kleed mee). Bedrag in euro's.
const SHIPPING_COST = 6.5;
// Vaste aanbiedingskorting op het ondertapijt (zelfde als de upsell-aanbieding).
const AANBIEDING_DISCOUNT = 30;

export function NabestelOndertapijtVerzendenDialog({ orderId, onClose, onDone }: Props) {
  const [eligibility, setEligibility] = useState<UpsellEligibility | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<NabestelOndertapijtResult | null>(null);
  const [copied, setCopied] = useState(false);
  const errorRef = useRef<HTMLParagraphElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getUpsellEligibility(orderId, 'ondertapijt', AANBIEDING_DISCOUNT)
      .then((data) => {
        if (cancelled) return;
        setEligibility(data);
        setSelectedIds(new Set(data.items.filter((i) => !i.unavailable).map((i) => i.id)));
        setLoading(false);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [error]);

  const items = eligibility?.items ?? [];

  const toggleItem = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const subtotal = useMemo(() => {
    let sum = 0;
    for (const item of items) {
      if (!selectedIds.has(item.id) || item.unavailable) continue;
      sum += item.priceWithDiscountInclVat;
    }
    return sum;
  }, [items, selectedIds]);

  const shipping = subtotal > 0 ? SHIPPING_COST : 0;
  const total = subtotal + shipping;
  const selectableCount = selectedIds.size;

  const handleSubmit = async () => {
    if (selectableCount === 0) return;
    setSubmitting(true);
    setError(null);
    const res = await nabestelOndertapijtVerzenden({
      workOrderId: orderId,
      itemIds: Array.from(selectedIds),
      discount: AANBIEDING_DISCOUNT,
    });
    if (!res.success) {
      setError(res.message || 'Nabestelling mislukt');
      setSubmitting(false);
      return;
    }
    setResult(res);
    setSubmitting(false);
    onDone();
  };

  const copyLink = async () => {
    if (!result?.paymentUrl) return;
    try {
      await navigator.clipboard.writeText(result.paymentUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard kan geweigerd worden — link is alsnog zichtbaar */
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">Ondertapijt nabestellen (verzenden)</h2>
          <p className="text-xs text-slate-500 mt-1">
            {eligibility?.workOrderNumber ? `${eligibility.workOrderNumber} — ` : ''}
            Apart verzonden via PostNL, tegen de aanbiedingsprijs + verzendkosten.
          </p>
        </div>

        <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
          {/* ── Resultaat ── */}
          {result ? (
            <div className="space-y-3">
              <div className="flex items-start gap-2 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">
                <svg className="w-5 h-5 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <div>
                  <p className="font-medium">Webshop-order {result.werkOrderName} aangemaakt</p>
                  <p className="text-emerald-700 text-xs mt-0.5">
                    Status: {result.status}. De klant ontvangt automatisch een Kleda-mail met de betaallink — hieronder staat 'ie ook om zelf te delen.
                  </p>
                </div>
              </div>

              {result.paymentUrl && (
                <div className="space-y-1">
                  <span className="text-xs text-slate-500">Betaallink (om handmatig te delen)</span>
                  <div className="flex items-center gap-2">
                    <input
                      readOnly
                      value={result.paymentUrl}
                      className="flex-1 min-w-0 px-2 py-1 text-xs border border-slate-300 rounded bg-slate-50 text-slate-700"
                    />
                    <button
                      type="button"
                      onClick={copyLink}
                      className="text-xs font-medium px-2.5 py-1.5 rounded border border-indigo-300 text-indigo-700 hover:bg-indigo-50 shrink-0"
                    >
                      {copied ? 'Gekopieerd' : 'Kopieer'}
                    </button>
                    <a
                      href={result.paymentUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs font-medium px-2.5 py-1.5 rounded border border-slate-300 text-slate-700 hover:bg-slate-50 shrink-0"
                    >
                      Open
                    </a>
                  </div>
                </div>
              )}

              {result.warning && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                  {result.warning}
                </p>
              )}
            </div>
          ) : (
            <>
              {loading && <p className="text-sm text-slate-500">Laden…</p>}

              {!loading && items.length === 0 && (
                <p className="text-sm text-slate-500">Geen kleden gevonden om een ondertapijt voor na te bestellen.</p>
              )}

              {!loading && items.length > 0 && (
                <div className="space-y-2">
                  {items.map((item) => {
                    const selected = selectedIds.has(item.id) && !item.unavailable;
                    return (
                      <label
                        key={item.id}
                        className={clsx(
                          'flex items-center gap-3 px-3 py-2 border rounded cursor-pointer',
                          item.unavailable
                            ? 'border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed'
                            : selected
                              ? 'border-indigo-300 bg-indigo-50/50'
                              : 'border-slate-200 hover:border-slate-300'
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleItem(item.id)}
                          disabled={item.unavailable}
                          className="h-4 w-4"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-slate-900 truncate">{item.name}</div>
                          <div className="text-xs text-slate-500 flex items-center gap-2">
                            {item.m2 != null && <span>{item.m2.toFixed(2)} m²</span>}
                            {item.tweedelig && <span className="text-amber-600">2-delig</span>}
                            {item.unavailable && (
                              <span className="text-rose-600">{item.unavailableReason || 'Niet leverbaar'}</span>
                            )}
                          </div>
                        </div>
                        <div className="text-sm text-slate-900">{formatCurrency(item.priceWithDiscountInclVat)}</div>
                      </label>
                    );
                  })}
                </div>
              )}

              {!loading && items.length > 0 && (
                <div className="mt-4 pt-3 border-t border-slate-100 space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Ondertapijt (aanbieding −{AANBIEDING_DISCOUNT}%)</span>
                    <span className="text-slate-900">{formatCurrency(subtotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Verzendkosten (PostNL)</span>
                    <span className="text-slate-900">{formatCurrency(shipping)}</span>
                  </div>
                  <div className="flex justify-between font-medium pt-1.5 border-t border-slate-100">
                    <span className="text-slate-700">Totaal incl. BTW</span>
                    <span className="text-slate-900">{formatCurrency(total)}</span>
                  </div>
                  <p className="text-[11px] text-slate-400 pt-1">
                    De klant ontvangt automatisch een Kleda-mail met de Mollie-betaallink. Verzending via PostNL.
                  </p>
                </div>
              )}

              {error && <p ref={errorRef} className="mt-3 text-xs text-red-600">{error}</p>}
            </>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          {result ? (
            <button
              type="button"
              onClick={onClose}
              className="text-sm font-medium px-3 py-1.5 rounded bg-indigo-600 text-white hover:bg-indigo-700"
            >
              Klaar
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting || loading || selectableCount === 0}
                className={clsx(
                  'text-sm font-medium px-3 py-1.5 rounded',
                  submitting || loading || selectableCount === 0
                    ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
                )}
              >
                {submitting ? 'Versturen…' : 'Versturen & betaallink aanmaken'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
